import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { errorMessage } from "@/shared/api/http";
import type { NavItemContext } from "@/shell/navigation";
import { useAppShell } from "@/shell/app-shell-context";
import {
  listVersaShortcuts,
  openTemplateToday,
  SHORTCUTS_CHANGED_EVENT,
  type VersaShortcut,
} from "./api";

/**
 * Os modelos que a pessoa fixou, logo abaixo de "Versa" no menu lateral
 * (atalho PESSOAL, decisão do time de 2026-10-30). Um clique abre o
 * relatório do dia daquele modelo — reabre o de hoje se já existe, ou cria
 * e pede de novo as bases do anterior — no "modo atualizar".
 *
 * Modelo desativado depois de fixado aparece desabilitado com o motivo,
 * em vez de sumir sem explicação.
 */
export function VersaShortcutsNav({
  companyId,
  close,
  expanded,
}: NavItemContext) {
  const { token } = useAppShell();
  const navigate = useNavigate();
  const [items, setItems] = useState<VersaShortcut[]>([]);
  const [opening, setOpening] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!companyId) {
      setItems([]);
      return;
    }
    listVersaShortcuts(token)
      .then(setItems)
      // Sem atalhos não é erro de tela: o menu segue sem eles.
      .catch(() => setItems([]));
  }, [token, companyId]);

  useEffect(() => {
    load();
    window.addEventListener(SHORTCUTS_CHANGED_EVENT, load);
    return () => window.removeEventListener(SHORTCUTS_CHANGED_EVENT, load);
  }, [load]);

  if (!companyId || items.length === 0) return null;

  const open = async (item: VersaShortcut) => {
    setOpening(item.template_id);
    setError(null);
    try {
      const today = await openTemplateToday(token, item.template_id);
      close();
      navigate(
        `/empresas/${companyId}/versa/${today.dashboard.id}?atualizar=1`,
        { state: { today } },
      );
    } catch (failure) {
      setError(
        errorMessage(failure, "Não foi possível abrir o relatório do dia."),
      );
    } finally {
      setOpening(null);
    }
  };

  return (
    <div
      className="sidebar-shortcuts"
      role="group"
      aria-label="Modelos fixados do Versa"
    >
      {items.map((item) => {
        const reason = item.active
          ? `Abrir o relatório de hoje do modelo "${item.name}"`
          : `O modelo "${item.name}" foi desativado. Tire o atalho na aba Modelos do Versa.`;
        return (
          <button
            key={item.template_id}
            type="button"
            className="sidebar-shortcut"
            onClick={() => void open(item)}
            disabled={!item.active || opening !== null}
            title={reason}
            data-tooltip={expanded ? undefined : item.name}
            aria-label={reason}
          >
            <CalendarClock size={15} aria-hidden="true" />
            <span>{opening === item.template_id ? "Abrindo…" : item.name}</span>
          </button>
        );
      })}
      {error && (
        <small className="sidebar-shortcut-error" role="alert">
          {error}
        </small>
      )}
    </div>
  );
}
